import { Decimal } from 'decimal.js';
import DOMPurify from 'dompurify';
import { isEmpty } from 'lodash';
import { sumAmount } from '../pages/trip/manageTrip/createTrip/stepsForm/proposalForm/calculations';
import { ILeg } from '../pages/trip/manageTrip/createTrip/stepsForm/proposalForm/interface/ILeg';
import { TRIP_STATUS_MAP_LIST, usCountryCode } from '../constant/constant';
import { CONTACT_TYPE, IS_TRIP_CANCELLED, TRIP_STATUS_KEY } from '../constant/enum';
import moment from 'moment';
import { ITask } from '../service/task';
import {
  mapValues,
  flatten,
  groupBy,
  orderBy,
} from 'lodash';
import { IAirportAddress } from '../service/trip';
import parsePhoneNumber from 'libphonenumber-js';
import { USA_COUNTRY_ID } from './constant';

interface Fee {
  id: number;
  name: string;
  amount: number;
}

export const breakPoint: DataType = {
  xSmall: 576,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
};

type DataType = {
  xSmall: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
};

export const getContactFullName = (personalInformation: any) => {
  return personalInformation.contactType === CONTACT_TYPE.INDIVIDUAL
    ? `${personalInformation.firstName || ''} ${personalInformation.lastName || ''}`
    : ` ${personalInformation.businessName || ''}`;
};

export const redirectToExternalUrl = (url: string) => {
  window.open(url, '_blank', 'noreferrer');
};

export const toDataURL = (url: any) =>
  fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

export const isArrayLengthGreaterThanOne = (arr: any) => {
  try {
    return Array.isArray(arr) && arr.length > 0;
  } catch (e) {
    return false;
  }
};

export function getKeyByValue(object: any, value: any) {
  return Object.keys(object).find((key) => object[key] === value);
}

// Format a number as a 2-decimal string using decimal.js so float64 representation
// errors don't affect rounding (e.g. 2.695 stored as 2.6949999999999998 renders as
// '2.70' instead of '2.69'). ROUND_HALF_UP matches the prior epsilon-based behavior.
export const toFixed2Safe = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {return '0.00';}
  return new Decimal(value).toFixed(2, Decimal.ROUND_HALF_UP);
};

export const convertCurrencyToNumber = (value:any) => {
  return Number(value?.replace(/[^0-9.]+/g,''));
};

export const isValidHttpUrl = (url: string): boolean => {
  // Combined regex for http, https, and www
  const urlRegex: RegExp = /^(https?:\/\/|www\.)[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
  return urlRegex.test(url);
};


export const validateEmail = (email: string) => {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
};

export function getAbsoluteValue(num: any) {
  try{
    return Math.abs(num);
  } catch(e) {
    return 0;
  }
}

export function validatePhoneNumber(phoneNumber: string, countryCode: string) {
  if (!phoneNumber || !countryCode) {
    return false;
  }
  const phone = String(phoneNumber);
  const country = String(countryCode);
  const digits = phone.replace(/\D/g, '');
  if (!digits) {
    return false;
  }
  const fullNumber = `+${country.replace(/\D/g, '')}${digits}`;
  try {
    const parsed = parsePhoneNumber(fullNumber);
    return parsed?.isValid() || false;
  } catch {
    return false;
  }
}

export function extractNumberFromString(val: string) {
  if (!val) {
    return '';
  }
  const integers = val.match(/-?\d+/g);
  if (integers) {
    const result = integers.map(number => number.replace(/-/, '')).join('');
    return result;
  } else {
    return '';
  }
}

export function maskPhoneNumber(
  phoneNumber: string = '',
  countryCode?: string,
  extension?: string
): string {
  if (!String(phoneNumber).trim()) {
    return '';
  }

  const digits = extractNumberFromString(phoneNumber);
  if (!digits) {
    return '';
  }

  const dialCode = countryCode || '1';
  const ext = extension?.trim() ? ' ext. ' + extension.trim() : '';

  try {
    const parsed = parsePhoneNumber('+' + dialCode + digits);
    if (parsed) {
      if (dialCode === '1') {
        return '+1 ' + parsed.formatNational() + ext;
      }
      return parsed.formatInternational() + ext;
    }
  } catch { /* fall through */ }

  return '+' + dialCode + ' ' + digits + ext;
}

export const formatName = (firstName: string, lastName: string, middleName?: string | null) => {
  const nameArray = [ firstName, (middleName || ''), lastName ];
  return nameArray.join(' ');
};

export function formatAddressRender(item: any) {
  const USA_COUNTRY_ID = 233;
  const stateName = item?.country?.id !== USA_COUNTRY_ID
    ? item?.addressesTypes?.stateName || ''
    : item?.state?.displayName || '';
  const address = [
    (item?.address || '') ,
    (item?.address2 || ''),
    (item?.city || ''),
    (stateName),
    (item?.zip || ''),
    (item?.country?.displayName || ''),
  ].filter((val) => !isEmpty(val.trim()));
  return address.join(', ');
}

export function formatAddressRenderNew(item: any) {
  const USA_COUNTRY_ID = 233;
  const stateName = item?.countryId !== USA_COUNTRY_ID
    ? item?.stateName || ''
    : item?.state?.displayName || '';
  const address = [
    (item.address || '') ,
    (item?.address2 || ''),
    (item?.city || ''),
    (stateName ? stateName : (item?.state ? item?.state?.displayName : '')),
    (item?.zip || ''),
    (item?.country?.displayName || ''),
  ].filter((val) => !isEmpty(val.trim()));
  return address.join(', ');
}
export function formatAddress(streetAddress: string | null, city: string | null, state: string | null, country: string | null) {
  const address = [
    (streetAddress || ''),
    (city|| ''),
    (state || ''),
    (country || ''),
  ].filter((val) => !isEmpty(val.trim()));
  return address.join(', ');
}

export const formatNumberToFixed = (num: number) => num ? num.toFixed(2) : '0';

export const feeCalculation = (data: Fee[]) => {
  const Handling_Fee = data?.find((item: Fee) => item?.id === 1)?.amount || 0;
  const Segment_Fee = data?.find((item: Fee) => item?.id === 2)?.amount || 0;
  const Federal_Excise_Tax = data?.find((item: Fee) => item?.id === 3)?.amount || 0;
  const International_Fee = data?.find((item: Fee) => item?.id === 4)?.amount || 0;
  const Special_Request = data?.filter((item: Fee) => ![
    1,
    2,
    3,
    4,
  ].includes(item?.id)).map((value: Fee) => value?.amount || 0);
  return {
    Handling_Fee: Handling_Fee,
    Segment_Fee: Segment_Fee,
    Federal_Excise_Tax: Federal_Excise_Tax,
    International_Fee: International_Fee,
    Special_Request: sumAmount(Special_Request),
  };
};

export const clientPayStatus = (dollars: number = 0,adjustment: number = 0) => (dollars + adjustment) !== 0;

export const downloadFileFromS3 = async (file: string, fileName: string) => {
  const responseData = await fetch(file);
  const blob = await responseData.blob();
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  reader.onloadend = () => {
    const dataURL = reader.result;
    const anchor: any = document.createElement('a');
    anchor.href = dataURL;
    anchor.download = fileName;
    anchor.click();
  };
};

export enum LegSourceType {
  DESTINATION = 'DESTINATION',
  ORIGIN = 'ORIGIN',
}

export const getLegFormatedAddress = (singleLeg: ILeg, legSourceType: LegSourceType) => {
  let addr1: any = '';
  let addr2: any  = '';
  let addr3: any = '';

  if(legSourceType === LegSourceType.ORIGIN) {
    addr1 = singleLeg?.originAirport?.airportAddress?.city;
    addr2 = singleLeg?.originAirport?.airportAddress?.country.id === usCountryCode ? singleLeg?.originAirport?.airportAddress?.state?.abbreviation : singleLeg?.originAirport?.airportAddress?.country?.countryCode;
    addr3 = singleLeg?.originAirport?.code;
  } else {
    addr1 = singleLeg?.destinationAirport?.airportAddress?.city;
    addr2 = singleLeg?.destinationAirport?.airportAddress?.country?.id === usCountryCode ? singleLeg?.destinationAirport?.airportAddress?.state?.abbreviation : singleLeg?.destinationAirport?.airportAddress?.country?.countryCode;
    addr3 = singleLeg?.destinationAirport?.code;
  }

  let fullAddress = '';
  if(addr1 && addr2) {
    fullAddress = `${addr1}, ${addr2}`;
  } else if(addr1 && !addr2) {
    fullAddress =  addr1;
  } else if(addr2 && !addr1) {
    fullAddress =  addr2;
  }
  return fullAddress + (addr3 ? `(${addr3})` : '');
};

export const getAirportLocationString = (airportAddress: IAirportAddress) => {
  let location = '';
  if (airportAddress.city) {
    location += (airportAddress.city + ', ');
  }
  if (airportAddress.country.id === usCountryCode) {
    if (airportAddress.state?.abbreviation) {
      location += airportAddress.state?.abbreviation;
    }
  } else if (airportAddress.country.countryCode) {
    location += airportAddress.country.countryCode;
  }
  return location;
};

export const checkIsLegDisableByTripStatusId = (statusId : number) => {
  const tripStatusDisableList = [
    TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.BROKER_TO_REVIEW].id,
    TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.COMPLETED].id,
    TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.FINALIZED].id,
    TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.CANCELLED].id,
    TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.CANCELLED_WITH_COST].id,
    TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.CANCELLED_AND_FINALIZED].id,
  ];
  return tripStatusDisableList.includes(statusId);
};

export const tripCancelWithCost = (statusId: any) => {
  return [ TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.CANCELLED_AND_FINALIZED].id, TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.CANCELLED_WITH_COST].id ].includes(statusId);
};

export const coerceToNumber = (value: any) => Number(value) || 0;

export const isTripCancelled = (trip: any): IS_TRIP_CANCELLED => {
  const isCancelTripWithoutCost = [ TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.CANCELLED].id ].includes(trip.tripStatusId);
  const isCancelTripWithCost = [ TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.CANCELLED_AND_FINALIZED].id, TRIP_STATUS_MAP_LIST[TRIP_STATUS_KEY.CANCELLED_WITH_COST].id ].includes(trip.tripStatusId);
  if(!isCancelTripWithCost && !isCancelTripWithoutCost) {
    return IS_TRIP_CANCELLED.NO;
  } else {
    return isCancelTripWithCost ? IS_TRIP_CANCELLED.CANCELLED_WITH_COST : IS_TRIP_CANCELLED.CANCELLED_WITHOUT_COST;
  }
};

/**
 * The calendar day an API instant falls on in UTC, as a `Date` at local midnight.
 *
 * maverick-core keeps the finance date fields (`ccHold`, `ccHoldExpiration`, `dueDate`) as
 * calendar days and reads and writes them at UTC — see `CcHoldExpirations` in that repo — but
 * sends them as instants. Since SKYL-1992 gave `ccHoldExpiration` a real time of day, a hold
 * re-authorized at 01:30 arrives as `...T01:30:00Z`, which `new Date()` resolves to the previous
 * day anywhere west of UTC; the panel then posts that day back and the stored expiration walks a
 * day earlier on every save. Normalising on the way in puts the pickers, the read-only view and
 * the save payload on the one day the backend meant, in any timezone.
 *
 * Takes the instant as the API sends it. Do not re-apply it to a value it already returned: east
 * of UTC, local midnight is the previous day in UTC and a second pass would shift the day.
 */
export const utcDayToLocalDate = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const utc = moment.utc(value);
  return utc.isValid() ? new Date(utc.year(), utc.month(), utc.date()) : null;
};

/**
 * The calendar day a picked `Date` falls on locally, as the UTC-midnight instant a grid filter
 * posts.
 *
 * The inverse of {@link utcDayToLocalDate}, for the way out. A Kendo date picker hands back a
 * `Date` in the browser's timezone, while the columns these filters target hold a calendar day at
 * UTC midnight (`tripview.cc_hold_expiration` is projected to the day on the way in - see
 * `upserttrip` in maverick-db) and `GridSpecificationBuilder` in maverick-core compares the posted
 * instant literally. Converting the picked `Date` to UTC first moves the day whenever local time
 * and UTC disagree on it - every value east of UTC, and every evening value west of it - so the
 * filter would run on the day either side of the one the user chose.
 *
 * Takes a picker's `Date` only, deliberately: an already-UTC instant would be read as its local day
 * and walk back a day west of UTC, which is the mistake this exists to prevent. An empty picker
 * (the filter state holds `''`) and an Invalid Date both give null, so a half-filled range posts
 * one bound and a cleared one posts none.
 */
export const localDayToUtcDayStart = (value: Date | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const picked = moment(value);
  return picked.isValid() ? `${picked.format('YYYY-MM-DD')}T00:00:00.000Z` : null;
};

export const MAX_ALLOWED_EXPORT_ROWS = 25000;
export const MAX_ALLOWED_EXPORT_ROWS_MESSAGE = 'Can’t export more than 25000 records. Please filter the records accordingly.';

export const loadScript = (src: string) => new Promise<void>((resolve, reject) => {
  const scriptElem = Object.assign(document.createElement('script'), {
    type: 'text/javascript',
    defer: true,
    src,
    onerror: (e: any) => {
      reject(e);
    },
  });
  scriptElem.onload = () => {
    resolve();
  };
  document.body.appendChild(scriptElem);
});

export const getExpiryDates = () => {
  const years: number[] = [];
  const months: string[] = [
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
  ];

  const currentYear = new Date().getFullYear();

  for (let i = 0; i < 7; i++) {
    years.push(currentYear + i);
  }

  return {
    months,
    years,
  };
};


export function sortTasksByDueDateAndPriority(tasks: Array<ITask>) {
  // Define the priority order
  const priorityOrder = {
    High: 1,
    Medium: 2,
    Low: 3,
  };

  // Group tasks by due date
  const groupedTasks = groupBy(tasks, (task) =>
    moment(task.dueDate, 'MM/DD/YYYY').format('MM/DD/YYYY')
  );

  // Sort tasks within each group by priority
  const sortedTasks = mapValues(groupedTasks, (group) =>
    orderBy(group, [ (task) => priorityOrder[task.priority] ], [ 'asc' ])
  );

  // Flatten the grouped and sorted tasks back into a single array
  const result = flatten(Object.values(sortedTasks));

  return result;
}

export const convertArrayIntoString = (array: Array<string>) => {
  return array.filter((item) => !isEmpty(item)).join(', ');
};

export const isJsonString = (jsonString: string) => {
  try {
    JSON.parse(jsonString);
  } catch (e) {
    return false;
  }
  return true;
};

const roundedNumber = (number : number) : number => Math.ceil(Number(number) * 100) / 100;

export const roundUpToTwoDecimalPlaces = (number: number): number => {
  if (String(number).split('.')?.length === 2 && String(number).split('.')[1]?.length > 2) {
    const value =
      Math.floor(number) +
      Number(String(number).split('.')[1].slice(0, 4)) /
        10 ** String(number).split('.')[1].slice(0, 4).length;
    return Number(parseFloat(roundedNumber(value).toFixed(2)).toFixed(2));
  } else {
    return Number(parseFloat(number.toFixed(2)).toFixed(2));
  }
};

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Linkify http(s) URLs for rendering via dangerouslySetInnerHTML. Free-text (task
// descriptions, trip notes) is HTML-escaped FIRST so it can't inject markup and a
// crafted URL can't break out of the attribute; the result is then run through
// DOMPurify (repo convention) as defense-in-depth, which also enforces safe href
// protocols. target=_blank pairs with rel=noopener noreferrer.
export const convertUrlsToLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const linked = escapeHtml(text).replace(
    urlRegex,
    (url) =>
      `<a href="${url}" class="cursor-pointer" style="color: #00106E" target="_blank" rel="noopener noreferrer">${url}</a>`
  );
  return DOMPurify.sanitize(linked, {
    ALLOWED_TAGS: [ 'a' ],
    ALLOWED_ATTR: [ 'href', 'class', 'style', 'target', 'rel' ],
    ADD_ATTR: [ 'target' ],
  });
};

export const isNumber = (value: any) => {
  return typeof value === 'number';
};

export const deleteCookie = (cookieName:any,domain:any) => {
  // Set the expiration date to a past date
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;domain=${domain};`;
};

export const setCookie = (cname:any, cvalue:any, exdays:any, domain:any) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = 'expires=' + d.toUTCString();
  document.cookie = `${cname}=${cvalue};${expires};path=${domain.path};domain=${domain.domain};`;
};

export const getCookie = (cname:any) => {
  let name = cname + '=';
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

export const isDomestic = (originId:number, destinationId:number) => {
  return originId === USA_COUNTRY_ID && destinationId === USA_COUNTRY_ID;
};

export const getAWSFileUrl = (url: string) => {
  const encodedUrl = encodeURIComponent(url);
  return `awsFilePath=${encodedUrl}`;
};

export const DAILY_MINIMUM_PATTERN = /Light:\s*[\d.]+\s*\|\s*Mid:\s*[\d.]+\s*\|\s*Super-Mid:\s*[\d.]+\s*\|\s*Heavy:\s*[\d.]+/g;
export const MINIMUM_HOURS_PLACEHOLDER = /_Minimum Hours_/g;

export const getDailyMinimum = (rates: any) => {
  if (rates?.length >= 4) {
    const sortedRates: any = [ ...rates ].sort(
      (a: any, b: any) => a.aircraftTypeId - b.aircraftTypeId
    );
    return `Light: ${sortedRates[0].minDailyHours} | Mid: ${sortedRates[1].minDailyHours} | Super-Mid: ${sortedRates[2].minDailyHours} | Heavy: ${sortedRates[3].minDailyHours}`;
  }
  return ' ';
};
